import {
  Card,
  CardContent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { ShiftResponseDto, WeekDays } from '@operations/app/api'
import { CustomTimePicker } from '@operations/components/form-controls/CustomTimePicker'
import { useTranslate } from '@tolgee/react'
import { RequiredField, useMinimalsTheme } from '@tom-ui/ui'
import { FormField, IFormStore } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { IShiftFormData } from '../../models/shift.model'

interface Props {
  formStore: IFormStore<IShiftFormData>
  shift: ShiftResponseDto | undefined
  shiftNames: string[]
}

export const ShiftForm = observer(({ formStore, shift, shiftNames }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  return (
    <Card>
      <CardContent>
        <Stack component='form' noValidate autoComplete='off' gap={theme.customSpacing.l}>
          <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='subtitle1'>{t('shiftDetails', 'Shift details')}</Typography>
          </Stack>

          <FormField
            formStore={formStore}
            name={'name'}
            initialValue={shift?.name ?? ''}
            validate={value => {
              if (!value) return t('fieldIsRequired', 'Field is required')
              else if (shiftNames.includes(value)) {
                return t('shiftNameAlreadyExists', 'Shift name already exists')
              }
            }}
          >
            {store => (
              <Stack>
                <TextField
                  {...store.props}
                  fullWidth
                  label={`${t('name', 'Name')}`}
                  required
                  variant='outlined'
                  type='text'
                  name='name'
                />
                <Typography variant='caption' color={store.error ? 'error' : 'text.secondary'}>
                  {store.error
                    ? store.error
                    : t('pleaseMakeSureItsAUniqueField', "Please make sure it's a unique {field}", {
                        field: t('name', 'name'),
                      })}
                </Typography>
              </Stack>
            )}
          </FormField>

          <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='subtitle1'>{t('time', 'Time')}</Typography>
          </Stack>
          <Stack flexDirection='row' alignItems='center' gap={theme.customSpacing.s}>
            <FormField
              formStore={formStore}
              name={'from'}
              initialValue={shift?.from ? dayjs(shift.from as any, 'HH:mm:ss') : null}
              validate={value => {
                if (!value) return t('fieldIsRequired', 'Field is required')
              }}
            >
              {store => (
                <Stack>
                  <CustomTimePicker
                    {...store.props}
                    label={t('from', 'From')}
                    ampm={false}
                    fullWidth
                    required
                    onChange={val => store.setValue(val)}
                    error={!!store.error}
                    value={store.value ?? undefined}
                  />
                  <Typography variant='caption' color={store.error ? 'error' : 'text.secondary'}>
                    {store.error ? t('fieldIsRequired') : t('24hrClock', '24hr clock')}
                  </Typography>
                </Stack>
              )}
            </FormField>
            -
            <FormField
              formStore={formStore}
              name={'to'}
              initialValue={shift?.to ? dayjs(shift.to as any, 'HH:mm:ss') : null}
              validate={value => {
                if (!value) return t('fieldIsRequired', 'Field is required')
              }}
            >
              {store => (
                <Stack>
                  <CustomTimePicker
                    {...store.props}
                    label={t('to', 'To')}
                    ampm={false}
                    fullWidth
                    required
                    onChange={val => store.setValue(val)}
                    error={!!store.error}
                    value={store.value ?? undefined}
                  />
                  <Typography variant='caption' color={store.error ? 'error' : 'text.secondary'}>
                    {store.error ? t('fieldIsRequired') : t('24hrClock', '24hr clock')}
                  </Typography>
                </Stack>
              )}
            </FormField>
          </Stack>

          <Stack flexDirection='row'>
            <Typography variant='subtitle1'>
              {t('weekDays', 'Week days')} <RequiredField />
            </Typography>
          </Stack>
          <Stack flexDirection='row' alignItems='center' gap={theme.customSpacing.s}>
            <FormField
              formStore={formStore}
              name={'weekDays'}
              initialValue={shift?.weekDays.split(',').map(day => day.trim()) ?? ([] as string[])}
              validate={value => {
                if (!value || value.length === 0) return t('fieldIsRequired', 'Field is required')
              }}
            >
              {store => (
                <Stack>
                  <ToggleButtonGroup
                    size='small'
                    value={store.value}
                    onChange={(_event, newValue) => store.setValue(newValue)}
                    aria-label='weekdays'
                    sx={{
                      gap: theme.customSpacing.xs,
                      '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                        marginLeft: 0, // prevent MUI’s default negative margin
                      },
                      border: 'none',
                      '& .MuiToggleButtonGroup-grouped': {
                        border: 'none',
                      },
                    }}
                  >
                    {[
                      WeekDays.Monday,
                      WeekDays.Tuesday,
                      WeekDays.Wednesday,
                      WeekDays.Thursday,
                      WeekDays.Friday,
                      WeekDays.Saturday,
                      WeekDays.Sunday,
                    ].map(day => (
                      <ToggleButton
                        key={day}
                        value={day}
                        aria-label={day}
                        sx={{
                          border: 'none',
                          paddingX: 1.2,
                          paddingY: 0.5,
                          backgroundColor: store.value.includes(day)
                            ? 'rgba(99, 115, 129, 1)!important'
                            : 'rgba(223, 227, 232, 1)!important',
                          color: store.value.includes(day)
                            ? 'white !important'
                            : 'black !important',
                          '&:hover': {
                            backgroundColor: 'rgba(99, 115, 129, 1)!important',
                            color: 'white !important',
                          },
                        }}
                      >
                        {day}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant='caption' color='error'>
                    {store.error}
                  </Typography>
                </Stack>
              )}
            </FormField>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
})
